import React, { useState, useCallback } from 'react';
import { useQuery } from "@tanstack/react-query";
import { searchProperties } from '../api/fetchRealtimeProperty';
import { SearchDropdown } from '@/designSystem/SearchableCombobox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/designSystem/Tooltip';
import { InfoIcon, LinkIcon } from 'lucide-react';
import { useDebounce } from '@uidotdev/usehooks';
import { extractBookingPath } from '@/utils';

interface PropertyResult {
  property_id: number;
  name: string;
  path: string;
  city: string;
  country_code: string;
}

interface PropertySearchProps {
  onSelect: (item: PropertyResult) => void;
}

const PropertySearch: React.FC<PropertySearchProps> = ({ onSelect }) => {
  const [searchInput, setSearchInput] = useState('');
  const [isUrlDetected, setIsUrlDetected] = useState(false);

  // Check for URL whenever search input changes
  const handleInputChange = (value: string) => {
    setSearchInput(value);
    const isUrl = extractBookingPath(value) !== null;
    setIsUrlDetected(isUrl);
  };

  // Process the search term - use path if URL detected
  const processedSearchTerm = isUrlDetected
    ? extractBookingPath(searchInput) || searchInput
    : searchInput;

  // Use the external debounce hook
  const debouncedSearchTerm = useDebounce(processedSearchTerm, 300);

  // Should we be showing loading state? Only if we have a valid search term
  const shouldShowLoading = searchInput.length >= 3;

  // Use TanStack Query for search
  const { data, isPending, isFetching } = useQuery({
    queryKey: ['propertySearch', debouncedSearchTerm],
    queryFn: async () => {
      if (!debouncedSearchTerm || debouncedSearchTerm.length < 3) {
        return { results: [] };
      }
      return searchProperties(debouncedSearchTerm);
    },
    enabled: debouncedSearchTerm.length >= 3,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    placeholderData: (previousData) => previousData,
  });

  // Only show loading state when we have a valid search term AND query is loading
  const isLoading = shouldShowLoading && (isPending || isFetching);

  // Handle search function for the dropdown
  const handleSearch = useCallback((): Promise<PropertyResult[]> => {
    // Return the current results from the cache or an empty array
    return Promise.resolve(data?.results || []);
  }, [data]);

  // Render function for property search results
  const renderPropertyItem = (property: PropertyResult) => (
    <div className="p-3">
      <div className="font-medium">{property.name}</div>
      <div className="text-sm text-muted-foreground">{property.city}, {property.country_code}</div>
      {property.path && (
        <div className="text-xs text-muted-foreground mt-1 truncate flex items-center">
          <LinkIcon className="h-3 w-3 mr-1" />
          booking.com{property.path}
        </div>
      )}
    </div>
  );

  const handleSelectProperty = (property: PropertyResult) => {
    onSelect?.(property);
    setSearchInput('');
  };

  return (
    <div className="relative">
      <SearchDropdown<PropertyResult>
        onSearch={handleSearch}
        renderItem={renderPropertyItem}
        onSelectItem={handleSelectProperty}
        getItemKey={(item) => item.property_id}
        placeholder="Search by booking.com URL or name"
        minSearchLength={3}
        emptyMessage="No properties found matching your search"
        loadingMessage="Searching properties..."
        isLoading={isLoading}
        onInputChange={handleInputChange}
      />
    </div>
  );
};

export default PropertySearch;